export const SLOT_CATALOG = [
  { start: '07:00:00', end: '09:00:00', label: '7–9 AM' },
  { start: '09:00:00', end: '11:00:00', label: '9–11 AM' },
  { start: '11:00:00', end: '13:00:00', label: '11 AM–1 PM' },
  { start: '13:00:00', end: '15:00:00', label: '1–3 PM' },
  { start: '15:00:00', end: '17:00:00', label: '3–5 PM' },
];

export const TIME_SLOTS = SLOT_CATALOG.map(s => s.label);
export const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Ritika day_id (1=Monday … 7=Sunday) ↔ grid day labels used by the availability table. */
export const DAY_ID_TO_LABEL = {
  1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun',
};
export const DAY_LABEL_TO_ID = Object.fromEntries(
  Object.entries(DAY_ID_TO_LABEL).map(([id, label]) => [label, Number(id)])
);
export const API_DAY_NAME_TO_LABEL = {
  Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu',
  Friday: 'Fri', Saturday: 'Sat', Sunday: 'Sun',
  Mon: 'Mon', Tue: 'Tue', Wed: 'Wed', Thu: 'Thu', Fri: 'Fri', Sat: 'Sat', Sun: 'Sun',
};

// Ritika API start_time values for each canonical slot label.
const SLOT_START_TIMES = {
  '7–9 AM':     '07:00:00',
  '7-9 AM':     '07:00:00',
  '9–11 AM':    '09:00:00',
  '9-11 AM':    '09:00:00',
  '11 AM–1 PM': '11:00:00',
  '11 AM-1 PM': '11:00:00',
  '11 AM - 1 PM': '11:00:00',
  '11AM-1PM':   '11:00:00',
  '1–3 PM':     '13:00:00',
  '1-3 PM':     '13:00:00',
  '1 - 3 PM':   '13:00:00',
  '3–5 PM':     '15:00:00',
  '3-5 PM':     '15:00:00',
  '3 - 5 PM':   '15:00:00',
};

// DB / API time_slot strings saved by mentor-api → canonical TIME_SLOTS label.
const DB_TIME_SLOT_TO_LABEL = {
  '07:00 - 09:00': '7–9 AM',
  '09:00 - 11:00': '9–11 AM',
  '11:00 - 13:00': '11 AM–1 PM',
  '13:00 - 15:00': '1–3 PM',
  '15:00 - 17:00': '3–5 PM',
};

function normalizeSlotKey(slot) {
  return String(slot || '')
    .replace(/\u2013/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeStartTime(value) {
  if (!value) return null;
  const parts = String(value).split(':');
  if (parts.length < 2) return null;
  return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
}

export function resolveSessionStartTime(slot) {
  if (!slot) return null;

  const direct = SLOT_START_TIMES[slot];
  if (direct) return direct;

  const normalized = normalizeSlotKey(slot);
  if (SLOT_START_TIMES[normalized]) return SLOT_START_TIMES[normalized];

  const dbMatch = normalized.match(/^(\d{2}:\d{2})\s*-\s*\d{2}:\d{2}$/);
  if (dbMatch) return `${dbMatch[1]}:00`;

  return null;
}

export function slotLabelFromStart(startTime) {
  if (!startTime) return '';
  const normalized = normalizeStartTime(startTime);
  return SLOT_CATALOG.find(s => s.start === normalized)?.label || '';
}

export function slotsMatch(a, b) {
  const startA = typeof a === 'object'
    ? (a?.slotStartTime || a?.slot_start_time || resolveSessionStartTime(a?.timeSlot || a?.time_slot))
    : resolveSessionStartTime(a);
  const startB = typeof b === 'object'
    ? (b?.slotStartTime || b?.slot_start_time || resolveSessionStartTime(b?.timeSlot || b?.time_slot))
    : resolveSessionStartTime(b);
  return Boolean(startA && startB && startA === startB);
}

export function formatSlotForApi(slot) {
  const start = resolveSessionStartTime(slot);
  if (!start) return {};
  const entry = SLOT_CATALOG.find(s => s.start === start);
  return {
    slot_start_time: start,
    time_slot: entry?.label || slot,
  };
}

export function normalizeTimeSlotLabel(raw, slotStartTime = null) {
  if (slotStartTime) {
    const fromStart = slotLabelFromStart(slotStartTime);
    if (fromStart) return fromStart;
  }
  if (!raw) return '';
  if (TIME_SLOTS.includes(raw)) return raw;

  const trimmed = String(raw).trim();
  if (DB_TIME_SLOT_TO_LABEL[trimmed]) return DB_TIME_SLOT_TO_LABEL[trimmed];

  const normalized = normalizeSlotKey(trimmed);
  if (SLOT_START_TIMES[normalized]) {
    return TIME_SLOTS.find(label => resolveSessionStartTime(label) === SLOT_START_TIMES[normalized]) || trimmed;
  }

  return trimmed;
}

/**
 * Resolve a slot row's weekday to grid label (Mon…Sun), or null if unknown.
 */
export function resolveAvailabilityDayLabel(slotOrDay) {
  if (slotOrDay == null || slotOrDay === '') return null;
  if (typeof slotOrDay === 'string' || typeof slotOrDay === 'number') {
    const asId = DAY_ID_TO_LABEL[Number(slotOrDay)];
    if (asId) return asId;
    const asName = API_DAY_NAME_TO_LABEL[slotOrDay] || (DAYS_OF_WEEK.includes(slotOrDay) ? slotOrDay : null);
    return asName || null;
  }
  const dayId = slotOrDay.dayId ?? slotOrDay.day_id;
  if (dayId != null && DAY_ID_TO_LABEL[Number(dayId)]) {
    return DAY_ID_TO_LABEL[Number(dayId)];
  }
  if (slotOrDay.day) {
    return API_DAY_NAME_TO_LABEL[slotOrDay.day] || (DAYS_OF_WEEK.includes(slotOrDay.day) ? slotOrDay.day : null);
  }
  return null;
}

/**
 * @param {Array} availabilitySlots - from normalizeAuditors (may include day / dayId)
 * @param {string} sessionSlot - session time slot label
 * @param {string|null} sessionDayOrDate - weekday label (Mon) or date YYYY-MM-DD; when set, day must match
 */
export function isAuditorAvailableForSlot(availabilitySlots, sessionSlot, sessionDayOrDate = null) {
  const slots = availabilitySlots || [];
  if (slots.length === 0) return false;

  let sessionDay = null;
  if (sessionDayOrDate) {
    const raw = String(sessionDayOrDate);
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      sessionDay = getDayOfWeek(raw.split(/[T ]/)[0]);
    } else {
      sessionDay = resolveAvailabilityDayLabel(raw);
    }
  }

  const sessionStartTime = resolveSessionStartTime(sessionSlot);
  const sessionKey = normalizeSlotKey(sessionSlot);

  return slots.some(s => {
    let timeOk = false;
    if (sessionStartTime) {
      const slotStart = normalizeStartTime(s.startTime) || resolveSessionStartTime(s.slot);
      timeOk = slotStart === sessionStartTime;
    } else {
      timeOk = normalizeSlotKey(s.slot) === sessionKey;
    }
    if (!timeOk) return false;

    // No session day provided → time-only match (legacy callers).
    if (!sessionDay) return true;

    const slotDay = resolveAvailabilityDayLabel(s);
    // Legacy flat slots without day: keep time-only so old rows still match.
    if (!slotDay) return true;

    return slotDay === sessionDay;
  });
}

export function isAuditorAvailable(auditorId, day, slot) {
  const sum = auditorId.length + day.length + slot.length;
  return sum % 3 === 0 || sum % 5 === 0;
}

export function getDayOfWeek(dateStr) {
  if (!dateStr) return '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const date = new Date(dateStr + 'T00:00:00');
  return days[date.getDay()];
}

export function getWeekLabel(dateStr) {
  if (!dateStr) return 'No Date Scheduled';
  const date = new Date(dateStr.length === 10 ? dateStr + 'T00:00:00' : dateStr);
  if (isNaN(date.getTime())) return 'No Date Scheduled';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dayOfWeek = date.getDay();
  const daysToMonday = (dayOfWeek === 0 ? -6 : 1 - dayOfWeek);
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() + daysToMonday);
  weekStart.setHours(0, 0, 0, 0);

  const todayDayOfWeek = today.getDay();
  const todayDaysToMonday = (todayDayOfWeek === 0 ? -6 : 1 - todayDayOfWeek);
  const thisWeekStart = new Date(today);
  thisWeekStart.setDate(today.getDate() + todayDaysToMonday);
  thisWeekStart.setHours(0, 0, 0, 0);

  const diffWeeks = Math.round((weekStart - thisWeekStart) / (7 * 24 * 60 * 60 * 1000));

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 4);
  const mon = `${MONTHS[weekStart.getMonth()]} ${weekStart.getDate()}`;
  const fri = `${MONTHS[weekEnd.getMonth()]} ${weekEnd.getDate()}`;
  const rangeLabel = `${mon}–${fri}`;

  if (diffWeeks === 0) return `This Week • ${rangeLabel}`;
  if (diffWeeks === 1) return `Next Week • ${rangeLabel}`;
  if (diffWeeks === -1) return `Last Week • ${rangeLabel}`;
  if (diffWeeks < 0) return `Past • ${rangeLabel}`;
  return rangeLabel;
}
